import {
  CancelPaymentControllerApi,
  Configuration,
  ConfigurationParameters,
  F1ControllerApi,
  FetchParams,
  ListActivePaymentControllerApi,
  LocationControllerApi,
  MoveOrderLinesControllerApi,
  PaymentPollingControllerApi,
  PendingPaymentControllerApi,
  ReceiptControllerApi,
  ResponseContext,
  SendPaymentReminderControllerApi,
  SplitPaymentControllerApi,
  SubmitCartControllerApi,
} from '../generated-api';
import config from './config';
import { useAuth } from './context/AuthContext';
import { useCurrentClientVersion, useLatestClientVersion, useVersionActions } from './context/VersionStore';

// Module-level map so deduplication works across renders.
const inFlight = new Map<string, Promise<Response>>();

/**
 * Wraps fetch to deduplicate concurrent identical mutating requests.
 * If the same POST/PUT/PATCH/DELETE to the same URL with the same body is
 * already in-flight, the second call receives the same promise instead of
 * firing a new network request.
 */
const dedupedFetch: typeof fetch = (input, init) => {
  const method = (init?.method ?? 'GET').toUpperCase();
  if (method === 'GET') return fetch(input, init);

  const url = input instanceof Request ? input.url : String(input);
  const key = `${method}:${url}:${init?.body ?? ''}`;

  const existing = inFlight.get(key);
  if (existing) return existing;

  const promise = (fetch(input, init) as Promise<Response>).finally(() => inFlight.delete(key));
  inFlight.set(key, promise);
  return promise;
};

export class ApiError extends Error {
  public traceId?: string;
  public title?: string;

  constructor(public response: Response, public errorBody?: unknown, message?: string, title?: string) {
    super(message || 'API call failed');
    this.name = 'ApiError';
    this.traceId = response.headers.get('x-trace-id') || undefined;
    this.title = title || 'Error';
  }
}

export const useApiClient = () => {
  const { getAccessToken } = useAuth();

  const latestClientVersion = useLatestClientVersion();
  const currentClientVersion = useCurrentClientVersion();
  const { setLatestClientVersion } = useVersionActions();

  const pre = async (context: ResponseContext): Promise<FetchParams | void> => {
    const accessToken = await getAccessToken();
    return {
      url: context.url,
      init: {
        ...context.init,
        headers: new Headers({
          ...context.init.headers,
          'x-client-version': currentClientVersion.toString(),
          Authorization: `Bearer ${accessToken}`,
        }),
      },
    };
  };
  const afterRequest = async (res: ResponseContext) => {
    const newLatestClientVersion = res.response.headers.get('x-latest-client-version');

    // Update latest available app version, if our local state is out of sync
    if (newLatestClientVersion && newLatestClientVersion != latestClientVersion?.toString()) {
      const parsedVersion = parseInt(newLatestClientVersion ?? '', 10);
      if (!Number.isNaN(parsedVersion)) {
        setLatestClientVersion(parsedVersion);
      }
    }

    if (!res.response.ok) {
      // Parse error details safely before rejecting
      const errorBody = await res.response.json().catch(() => null);

      const errorMessage =
        errorBody?.message || errorBody?.description || `HTTP ${res.response.status}: ${res.response.statusText}`;

      throw new ApiError(res.response, errorBody, errorMessage);
    }

    return res.response;
  };

  const configParams: ConfigurationParameters = {
    basePath: config.bff,
    fetchApi: dedupedFetch,
    middleware: [{ post: afterRequest, pre }],
  };
  const apiConfig = new Configuration(configParams);

  return {
    LocationControllerApi: new LocationControllerApi(apiConfig),
    SubmitCartControllerApi: new SubmitCartControllerApi(apiConfig),
    PaymentPollingController: new PaymentPollingControllerApi(apiConfig),
    PaymentCancelledController: new CancelPaymentControllerApi(apiConfig),
    ReceiptControllerApi: new ReceiptControllerApi(apiConfig),
    SendPaymentReminderControllerApi: new SendPaymentReminderControllerApi(apiConfig),
    MoveOrderLinesControllerApi: new MoveOrderLinesControllerApi(apiConfig),
    ListActivePaymentController: new ListActivePaymentControllerApi(apiConfig),
    SplitPaymentControllerApi: new SplitPaymentControllerApi(apiConfig),
    PendingPaymentControllerApi: new PendingPaymentControllerApi(apiConfig),
    F1ControllerApi: new F1ControllerApi(apiConfig),
  };
};
